import {
    Metamodel, AdapterRegistry, NamedAdapter, SharedModel,
    Credential, ModelConstants, ServicePrincipalCredentialProperties,
    CredentialTypeOptions, ManagedIdentityCredentialProperties,
    UserAssignedManagedIdentityCredentialProperties, UserAuthCredentialProperties,
    EmailCredentialProperties, TeamsCredentialProperties, IModel,
    AzureBlobCredentialProperties, AzureDevopsCredentialProperties, AzureDataFactoryCredentialProperties,
    AzureKeyVault,
    FederatedIdentityCredentialProperties
} from 'shell-models-lib';
import { akvToResource, akvToModel } from './linkedservices/azure-key-vault.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(Credential),
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            typeProperties: '*'
        }
    }
})
export class CredentialAdapter extends NamedAdapter<Credential, SharedModel.CredentialResource> {

    public model(): IModel {
        return Credential.MODEL;
    }

    protected _toResource(named: Credential): SharedModel.CredentialResource {
        const credRes = super._toResource(named);
        credRes.properties = credRes.properties || {};

        if (named.properties instanceof ServicePrincipalCredentialProperties) {
            const spnCredProperties = named.properties as ServicePrincipalCredentialProperties;
            credRes.properties.type = ModelConstants.servicePrincipal;
            credRes.properties.typeProperties = {
                tenant: spnCredProperties.tenant,
                servicePrincipalId: spnCredProperties.servicePrincipalId,
                servicePrincipalKey: spnCredProperties.servicePrincipalKey instanceof AzureKeyVault ?
                    akvToResource(spnCredProperties.servicePrincipalKey, this.context) :
                    this._ficToResource(spnCredProperties.servicePrincipalKey),
                azureCloudType: spnCredProperties.azureCloudType,
                servicePrincipalAuthority: spnCredProperties.servicePrincipalAuthority
            };
        } else if (named.properties instanceof ManagedIdentityCredentialProperties) {
            credRes.properties.type = CredentialTypeOptions.ManagedIdentity;
            credRes.properties.typeProperties = {};

            if (named.properties instanceof UserAssignedManagedIdentityCredentialProperties) {
                const uamiCredProperties = named.properties as UserAssignedManagedIdentityCredentialProperties;
                // if it's UAMI, overwrite the typeproperties.
                credRes.properties.typeProperties = {
                    resourceId: uamiCredProperties.resourceId
                };
            }
        } else if (named.properties instanceof UserAuthCredentialProperties) {
            credRes.properties.type = CredentialTypeOptions.UserAuth;
            credRes.properties.typeProperties = {
                token: named.properties.token
            };
        } else if (named.properties instanceof EmailCredentialProperties) {
            credRes.properties.type = CredentialTypeOptions.EmailAuth;
            credRes.properties.typeProperties = {
                kind: 'V1',
                properties: {
                    displayName: named.properties.email,
                    authenticatedUser: {
                        name: named.properties.email
                    },
                    api: {
                        name: 'office365',
                        category: 'Standard',
                        type: 'Microsoft.Web/locations/managedApis'
                    }
                },
                type: 'Microsoft.Web/connections'
            };
        } else if (named.properties instanceof TeamsCredentialProperties) {
            credRes.properties.type = CredentialTypeOptions.TeamsAuth;
            credRes.properties.typeProperties = {
                kind: 'V1',
                properties: {
                    displayName: named.properties.email,
                    authenticatedUser: {
                        name: named.properties.email
                    },
                    api: {
                        name: 'teams',
                        category: 'Standard',
                        type: 'Microsoft.Web/locations/managedApis'
                    }
                },
                type: 'Microsoft.Web/connections'
            };
        } else if (named.properties instanceof AzureBlobCredentialProperties) {
            credRes.properties.type = CredentialTypeOptions.AzureBlobAuth;
            credRes.properties.typeProperties = {
                kind: 'V1',
                properties: {
                    displayName: named.properties.email,
                    authenticatedUser: {
                        name: named.properties.email
                    },
                    api: {
                        name: 'azureblob',
                        category: 'Standard',
                        type: 'Microsoft.Web/locations/managedApis'
                    }
                },
                type: 'Microsoft.Web/connections'
            };
        } else if (named.properties instanceof AzureDevopsCredentialProperties) {
            credRes.properties.type = CredentialTypeOptions.AzureDevopsAuth;
            credRes.properties.typeProperties = {
                kind: 'V1',
                properties: {
                    displayName: named.properties.email,
                    authenticatedUser: {
                        name: named.properties.email
                    },
                    api: {
                        name: 'visualstudioteamservices',
                        category: 'Standard',
                        type: 'Microsoft.Web/locations/managedApis'
                    }
                },
                type: 'Microsoft.Web/connections'
            };
        } else if (named.properties instanceof AzureDataFactoryCredentialProperties) {
            credRes.properties.type = CredentialTypeOptions.AzureDataFactoryAuth;
            credRes.properties.typeProperties = {
                kind: 'V1',
                properties: {
                    displayName: named.properties.email,
                    authenticatedUser: {
                        name: named.properties.email
                    },
                    api: {
                        name: 'azuredatafactory',
                        category: 'Standard',
                        type: 'Microsoft.Web/locations/managedApis'
                    }
                },
                type: 'Microsoft.Web/connections'
            };
        }

        return credRes;
    }

    protected _toModel(resource: SharedModel.CredentialResource, model?: Credential): Credential {
        if (!model) {
            model = Credential.MODEL.create<Credential>();
        }

        super._toModel(resource, model);

        if (resource.properties) {
            if (resource.properties.typeProperties &&
                resource.properties.type === ModelConstants.servicePrincipal) {
                const spnCredProperties = new ServicePrincipalCredentialProperties();
                const credResProperties = resource.properties.typeProperties;
                spnCredProperties.tenant = credResProperties.tenant;
                spnCredProperties.servicePrincipalId = credResProperties.servicePrincipalId;
                spnCredProperties.servicePrincipalKey = credResProperties.servicePrincipalKey.type === ModelConstants.federatedIdentityCredentialProperties ?
                    this._ficToModel(credResProperties.servicePrincipalKey) : akvToModel(credResProperties.servicePrincipalKey, this.context);
                spnCredProperties.azureCloudType = credResProperties.azureCloudType;
                spnCredProperties.servicePrincipalAuthority = credResProperties.servicePrincipalAuthority;
                model.properties = spnCredProperties;
            } else if (resource.properties.type === CredentialTypeOptions.ManagedIdentity) {
                if (resource.properties.typeProperties && Object.keys(resource.properties.typeProperties).length > 0) {
                    // User Assigned Managed Identity.
                    const uamiCredProperties = new UserAssignedManagedIdentityCredentialProperties();
                    const credResProperties = resource.properties.typeProperties;
                    uamiCredProperties.resourceId = credResProperties.resourceId;
                    model.properties = uamiCredProperties;
                } else {
                    // System Assigned Managed Identity.
                    model.properties = new ManagedIdentityCredentialProperties();
                    model.isUxModel = resource.isUxModel;
                }
            } else if (resource.properties.type === CredentialTypeOptions.UserAuth) {
                const userAuthProperties = new UserAuthCredentialProperties();
                const credResProperties = resource.properties.typeProperties;
                userAuthProperties.token = credResProperties?.token;
                userAuthProperties.principalId = credResProperties?.principalId;
                model.properties = userAuthProperties;
            } else if (resource.properties.type === CredentialTypeOptions.EmailAuth) {
                const emailAuthProperties = new EmailCredentialProperties();
                emailAuthProperties.email = resource.properties.typeProperties?.properties?.authenticatedUser?.name;
                model.properties = emailAuthProperties;
            } else if (resource.properties.type === CredentialTypeOptions.TeamsAuth) {
                const teamsAuthProperties = new TeamsCredentialProperties();
                teamsAuthProperties.email = resource.properties.typeProperties?.properties?.authenticatedUser?.name;
                model.properties = teamsAuthProperties;
            } else if (resource.properties.type === CredentialTypeOptions.AzureBlobAuth) {
                const azureBlobAuthProperties = new AzureBlobCredentialProperties();
                azureBlobAuthProperties.email = resource.properties.typeProperties?.properties?.authenticatedUser?.name;
                model.properties = azureBlobAuthProperties;
            } else if (resource.properties.type === CredentialTypeOptions.AzureDevopsAuth) {
                const azureDevopsAuthProperties = new AzureDevopsCredentialProperties();
                azureDevopsAuthProperties.email = resource.properties.typeProperties?.properties?.authenticatedUser?.name;
                model.properties = azureDevopsAuthProperties;
            } else if (resource.properties.type === CredentialTypeOptions.AzureDataFactoryAuth) {
                const azureDataFactoryAuthProperties = new AzureDataFactoryCredentialProperties();
                azureDataFactoryAuthProperties.email = resource.properties.typeProperties?.properties?.authenticatedUser?.name;
                model.properties = azureDataFactoryAuthProperties;
            }
        }
        return model;
    }

    private _ficToResource(fic: FederatedIdentityCredentialProperties): SharedModel.FederatedIdentityCredentialResource {
        const sourceServicePrincipal: {
            servicePrincipalId?: string,
            servicePrincipalAuthority?: string,
            tenant?: string,
            servicePrincipalFmiPath?: string
        } = {};
        if (fic.servicePrincipalId) {
            sourceServicePrincipal.servicePrincipalId = fic.servicePrincipalId;
        }
        if (fic.servicePrincipalFmiPath) {
            sourceServicePrincipal.servicePrincipalFmiPath = fic.servicePrincipalFmiPath;
        }
        if (fic.servicePrincipalAuthority) {
            sourceServicePrincipal.servicePrincipalAuthority = fic.servicePrincipalAuthority;
        }
        if (fic.tenant) {
            sourceServicePrincipal.tenant = fic.tenant;
        }
        return {
            type: ModelConstants.federatedIdentityCredentialProperties,
            typeProperties: {
                resourceId: fic.resourceId,
                principalId: fic.principalId,
                sourceServicePrincipal: Object.keys(sourceServicePrincipal).length === 0 ? undefined : sourceServicePrincipal
            }
        };
    }

    private _ficToModel(resource: SharedModel.FederatedIdentityCredentialResource): FederatedIdentityCredentialProperties {
        const fic = new FederatedIdentityCredentialProperties();
        fic.resourceId = resource.typeProperties.resourceId;
        fic.principalId = resource.typeProperties.principalId;
        fic.servicePrincipalAuthority = resource.typeProperties.sourceServicePrincipal?.servicePrincipalAuthority;
        fic.servicePrincipalFmiPath = resource.typeProperties.sourceServicePrincipal?.servicePrincipalFmiPath;
        fic.servicePrincipalId = resource.typeProperties.sourceServicePrincipal?.servicePrincipalId;
        fic.tenant = resource.typeProperties.sourceServicePrincipal?.tenant;
        return fic;
    }
}
